/**
 * useRoleAccess -- central hook for role-based UI decisions.
 *
 * Every component that needs to show/hide based on the user's role
 * should use this hook instead of checking user.role directly.
 * This keeps the role hierarchy in one place.
 *
 * The hierarchy (each level includes everything below it):
 *   OWNER > ADMIN > PROGRAM_DIRECTOR > ACCOUNTANT > STAFF > MENTOR > BOARD > VOLUNTEER > EXTERNAL
 */
import { useCurrentUser } from "@/features/auth/model/useAuth";

/** Role hierarchy from highest to lowest privilege */
const ROLE_HIERARCHY = [
  "OWNER",
  "ADMIN",
  "PROGRAM_DIRECTOR",
  "ACCOUNTANT",
  "STAFF",
  "MENTOR",
  "BOARD",
  "VOLUNTEER",
  "EXTERNAL",
] as const;

type RoleName = (typeof ROLE_HIERARCHY)[number];

/** Accounting-only shell home. Distinct from `isAccountant` (accountant or above). */
export const ACCOUNTANT_HOME_PATH = "/accounting";

/** True only for the tenant ACCOUNTANT role — not Owner/Admin. */
export function isAccountantRole(role: string | null | undefined): boolean {
  return role === "ACCOUNTANT";
}

export function homePathForRole(role: string | null | undefined): string {
  return isAccountantRole(role) ? ACCOUNTANT_HOME_PATH : "/";
}

/** Returns the numeric level of a role (0 = highest, 6 = lowest) */
function roleLevel(role: string): number {
  const idx = ROLE_HIERARCHY.indexOf(role as RoleName);
  return idx === -1 ? 999 : idx; // unknown roles get lowest access
}

/** True if userRole is at or above the required minimum */
function meetsMinimum(userRole: string, minimumRole: string): boolean {
  return roleLevel(userRole) <= roleLevel(minimumRole);
}

export function useRoleAccess() {
  const user = useCurrentUser();
  const role = user?.role ?? "EXTERNAL";
  const userType = user?.user_type ?? "tenant";

  return {
    /** The raw role string */
    role,

    /** The user type (tenant, control, evaluator) */
    userType,

    /** True if the user is a control-plane admin */
    isControl: userType === "control",

    /** True if the user is a pilot evaluator */
    isEvaluator: userType === "evaluator",

    /** True if the user is Owner */
    isOwner: role === "OWNER",

    /** True if the user is Admin or above */
    isAdmin: meetsMinimum(role, "ADMIN"),

    /** True if the user is Accountant or above */
    isAccountant: meetsMinimum(role, "ACCOUNTANT"),

    /** True only for tenant ACCOUNTANT (home path is Accounting). */
    isAccountantRole: role === "ACCOUNTANT",

    /** True if the user is Staff or above */
    isStaff: meetsMinimum(role, "STAFF"),

    /** True if the user is Board or above */
    isBoard: meetsMinimum(role, "BOARD"),

    /** True if the user is Volunteer or above */
    isVolunteer: meetsMinimum(role, "VOLUNTEER"),

    /** Check if the user meets a specific minimum role */
    hasMinRole: (minimum: string) => meetsMinimum(role, minimum),

    /** Check if the user's role is in a specific list */
    hasRole: (...roles: string[]) => roles.includes(role),

    /** True if the user can write (create/edit/delete records) */
    canWrite: meetsMinimum(role, "STAFF"),

    /** True if the user can do financial writes */
    canWriteFinancials: meetsMinimum(role, "ACCOUNTANT"),

    /** True if the user can manage the team */
    canManageTeam: meetsMinimum(role, "ADMIN"),

    /** True if the user can manage billing */
    canManageBilling: role === "OWNER",
  };
}

/**
 * Role constants for use in requiresRole arrays on nav items.
 * These define which roles can SEE each navigation item.
 */
export const NAV_ROLES = {
  /** Everyone with a login sees this */
  ALL: ["OWNER", "ADMIN", "ACCOUNTANT", "STAFF", "BOARD", "VOLUNTEER"],

  /** Operational staff and above */
  OPERATIONS: ["OWNER", "ADMIN", "ACCOUNTANT", "STAFF"],

  /** Management only */
  MANAGEMENT: ["OWNER", "ADMIN"],

  /** Financial users */
  FINANCE: ["OWNER", "ADMIN", "ACCOUNTANT"],

  /** Owner only */
  OWNER_ONLY: ["OWNER"],

  /** Volunteer self-service */
  VOLUNTEER_SELF: ["OWNER", "ADMIN", "STAFF", "VOLUNTEER"],
} as const;
